import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  Loader,
  PasswordInput,
  Select,
  Space,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'tabler-icons-react';
import { cities, provinces } from '../../data/province-city.array';
import { Countries } from '../../enums/countries.enum';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { UpdateAccountSchema } from '../../schemas/update-account.schema';
import { ErrorResponse } from '../../type/error-response.types';
import { Cities } from '../../type/province-city.types';
import { RetrieveAccountResponse } from '../../type/retrieve-account.types';
import { UpdateAccountRequest } from '../../type/update-account.types';
import BackButton from '../buttons/back-button.component';

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  selectedId: number;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}

function AdminUpdateUser(props: Props) {
  const { setComponentManager, selectedId, setRefreshTable } = props;
  const { auth } = useAuth();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [newProvinces, setNewProvinces] = useState(provinces);
  const [newCities, setNewCities] = useState(cities);
  const [isFetched, setIsFetched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      !provinces.some((province: { value: string; label: string }) => {
        if (province.value === form.values.state!) {
          return true;
        }
        return false;
      })
    ) {
      setNewProvinces([
        ...provinces,
        {
          key: form.values.state!,
          value: form.values.state!,
          label: form.values.state!,
        },
      ]);
      const newProvince = {
        [form.values.state!]: [
          {
            key: form.values.city!,
            value: form.values.city!,
            label: form.values.city!,
          },
        ],
      };
      const newCities: Cities = {
        ...cities,
        ...newProvince,
      };
      setNewCities(newCities);
    } else if (
      !cities[form.values.state!].some(
        (city: { value: string; label: string }) => {
          if (city.value === form.values.city!) {
            return true;
          }
          return false;
        }
      )
    ) {
      setNewCities({
        ...newCities,
        [form.values.state!]: [
          ...newCities[form.values.state!],
          {
            key: form.values.city!,
            value: form.values.city!,
            label: form.values.city!,
          },
        ],
      });
    }
  }, [isFetched]);

  const form = useForm<{
    email: string;
    primary: string;
    mobile: string;

    firstName: string;
    middleName: string;
    noMiddleName: boolean;
    lastName: string;
    username: string;
    password: string;
    confirmPassword: string;
    pin: string;

    city: string;
    line1: string;
    buildingNameAndNumber: string;
    state: string;
    postalCode: string;
    barangay: string;
    countryId: number;
    fax: string;
  }>({
    initialValues: {
      email: '',
      primary: '',
      mobile: '',

      firstName: '',
      middleName: '',
      noMiddleName: false,
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',
      pin: '',

      city: '',
      line1: '',
      buildingNameAndNumber: '',
      state: '',
      postalCode: '',
      barangay: '',
      countryId: SENTINEL,
      fax: '',
    },
    schema: joiResolver(UpdateAccountSchema),
  });

  const fetchUser = async () => {
    try {
      const response = await axiosPrivate.get(`/web/accounts/${selectedId}`);
      const retrievedUser: RetrieveAccountResponse = response.data;

      form.setValues({
        email: retrievedUser.contact.email,
        primary: retrievedUser.contact.phone.primary,
        mobile: retrievedUser.contact.phone.mobile || '',

        firstName: retrievedUser.user.firstName,
        middleName: retrievedUser.user.middleName || '',
        noMiddleName:
          retrievedUser.user.middleName === 'N/A' ||
          retrievedUser.user.middleName === '.',
        lastName: retrievedUser.user.lastName,
        username: retrievedUser.user.username,
        password: retrievedUser.user.password,
        confirmPassword: retrievedUser.user.password,
        pin: retrievedUser.user.pin,

        city: retrievedUser.user.address?.city || '',
        line1: retrievedUser.user.address?.line1 || '',
        buildingNameAndNumber:
          retrievedUser.user.address?.buildingNameAndNumber || '',
        state: retrievedUser.user.address?.state || '',
        postalCode: retrievedUser.user.address?.postalCode || '',
        barangay: retrievedUser.user.address?.barangay || '',
        countryId: retrievedUser.user.address?.countryId || -999,
        fax: retrievedUser.user.address?.fax || '',
      });
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-fetch-user-data',
        message:
          error.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsFetched(true);
    }
  };
  useEffect(() => {
    fetchUser();
  }, []);

  const allowFullAccess =
    auth?.role === Roles.ADMINISTRATOR || auth?.id === selectedId;

  const handleSubmit = async () => {
    const values = form.values;
    const request: UpdateAccountRequest = {
      contact: {
        email: values.email,
        phone: {
          primary: values.primary,
          mobile: values.mobile,
        },
      },

      user: {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        username: values.username,
        password: values.password,
        pin: values.pin,
        address: {
          city: values.city!,
          line1: values.line1,
          buildingNameAndNumber: values.buildingNameAndNumber,
          state: values.state!,
          postalCode: values.postalCode,
          barangay: values.barangay,
          countryId: values.countryId,
          fax: values.fax,
        },
      },
    };

    if (
      request.contact.phone.mobile !== undefined &&
      request.contact.phone.mobile.trim().length < 1
    ) {
      delete request.contact.phone.mobile;
    }

    if (
      request.user.address?.fax !== undefined &&
      request.user.address?.fax.trim().length < 1
    ) {
      delete request.user.address?.fax;
    }
    if (
      request.user.address?.barangay !== undefined &&
      request.user.address?.barangay.trim().length < 1
    ) {
      delete request.user.address?.barangay;
    }
    if (
      request.user.address?.buildingNameAndNumber !== undefined &&
      request.user.address?.buildingNameAndNumber.trim().length < 1
    ) {
      delete request.user.address?.buildingNameAndNumber;
    }

    // if(!allowFullAccess) {
    //   delete request.user.username;
    //   delete request.user.username;
    // }

    setLoading(true);

    try {
      await axiosPrivate.put(`/web/accounts/${selectedId}`, request);

      showNotification({
        id: 'successfully-updated-user',
        message: 'Successfully updated user',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });

      setRefreshTable((prev) => !prev);
      fetchUser();
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-update-account',
        message: error?.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container fluid>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          Update User
        </Title>
        <Divider my='xl' />
        {!isFetched ? (
          <Loader />
        ) : (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Text size='xl' weight={700}>
              User Information
            </Text>
            <Space h='xl' />
            <Grid>
              <ResponsiveCol444>
                <TextInput
                  label={labels.firstName}
                  placeholder={placeholders.firstName}
                  {...form.getInputProps('firstName')}
                />
              </ResponsiveCol444>
              <ResponsiveCol444>
                <TextInput
                  label={labels.middleName}
                  placeholder={placeholders.middleName}
                  disabled={form.values.noMiddleName}
                  {...form.getInputProps('middleName')}
                />
                <Space my='xs' />
                <Checkbox
                  label="I don't have a middle name"
                  checked={form.values.noMiddleName}
                  onChange={() => {
                    const noMiddleName = !form.values.noMiddleName;
                    form.setFieldValue('noMiddleName', noMiddleName);
                    form.setFieldValue('middleName', noMiddleName ? '.' : '');
                  }}
                />
              </ResponsiveCol444>
              <ResponsiveCol444>
                <TextInput
                  label={labels.lastName}
                  placeholder={placeholders.lastName}
                  {...form.getInputProps('lastName')}
                />
              </ResponsiveCol444>
              {allowFullAccess && (
                <>
                  <ResponsiveCol444>
                    <TextInput
                      label={labels.username}
                      placeholder={placeholders.username}
                      {...form.getInputProps('username')}
                    />
                  </ResponsiveCol444>
                  <ResponsiveCol444>
                    <PasswordInput
                      label={labels.password}
                      placeholder={placeholders.password}
                      {...form.getInputProps('password')}
                    />
                  </ResponsiveCol444>
                  <ResponsiveCol444>
                    <PasswordInput
                      label={labels.confirmPassword}
                      placeholder={placeholders.confirmPassword}
                      {...form.getInputProps('confirmPassword')}
                    />
                  </ResponsiveCol444>
                  <ResponsiveCol444>
                    <PasswordInput
                      label={labels.pin}
                      placeholder={placeholders.pin}
                      autoComplete='new-password'
                      {...form.getInputProps('pin')}
                      maxLength={4}
                    />
                  </ResponsiveCol444>
                </>
              )}
              <ResponsiveCol444>
                <TextInput
                  label={labels.emailAddress}
                  placeholder={placeholders.emailAddress}
                  {...form.getInputProps('email')}
                />
              </ResponsiveCol444>
            </Grid>
            <Divider my='xl' />
            <Text size='xl' weight={700}>
              Address
            </Text>
            <Space h='xl' />
            <Grid>
              <Grid.Col span={12}>
                <TextInput
                  label={labels.bldgNameNo}
                  placeholder={placeholders.bldgNameNo}
                  {...form.getInputProps('buildingNameAndNumber')}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label={labels.streetAddress}
                  placeholder={placeholders.streetAddress}
                  {...form.getInputProps('line1')}
                />
              </Grid.Col>
              <ResponsiveCol66>
                <Select
                  label={labels.province}
                  placeholder={placeholders.province}
                  data={newProvinces}
                  value={form.values.state}
                  onChange={(value: string) => {
                    form.setFieldValue('state', value);
                    form.setFieldValue('city', '');
                  }}
                  searchable
                  nothingFound='No options'
                  error={form.errors.state}
                />
              </ResponsiveCol66>
              <ResponsiveCol66>
                <Select
                  label={labels.city}
                  placeholder={placeholders.city}
                  data={
                    form.values.state &&
                    form.values.state.length > 0 &&
                    Object.keys(newCities).includes(form.values.state)
                      ? newCities[form.values.state]
                      : []
                  }
                  disabled={form.values.state === ''}
                  searchable
                  value={form.values.city}
                  onChange={(value: string) => {
                    form.setFieldValue('city', value);
                  }}
                  error={form.errors.city}
                />
              </ResponsiveCol66>
              <ResponsiveCol444>
                <TextInput
                  label={labels.barangay}
                  placeholder={placeholders.barangay}
                  {...form.getInputProps('barangay')}
                />
              </ResponsiveCol444>
              <ResponsiveCol444>
                <TextInput
                  label={labels.postalCode}
                  placeholder={placeholders.postalCode}
                  {...form.getInputProps('postalCode')}
                />
              </ResponsiveCol444>
              <ResponsiveCol444>
                <Select
                  label={labels.country}
                  placeholder={placeholders.country}
                  data={[
                    {
                      label: 'Philippines',
                      value: Countries.PHILIPPINES.toString(),
                    },
                  ]}
                  value={form.values.countryId.toString()}
                  onChange={(value: string) => {
                    form.setFieldValue('countryId', parseInt(value));
                  }}
                  error={form.errors.countryId}
                />
              </ResponsiveCol444>
            </Grid>
            <Divider my='xl' />
            <Text size='xl' weight={700}>
              Contact Information
            </Text>
            <Space h='xl' />
            <Grid>
              <ResponsiveCol66>
                <TextInput
                  label={labels.contactNumber}
                  placeholder={placeholders.contactNumber}
                  {...form.getInputProps('primary')}
                />
              </ResponsiveCol66>
              <ResponsiveCol66>
                <TextInput
                  label={labels.mobileNumber}
                  placeholder={placeholders.mobileNumber}
                  {...form.getInputProps('mobile')}
                />
              </ResponsiveCol66>
              <Grid.Col xs={12}>
                <TextInput
                  label={labels.fax}
                  placeholder={placeholders.fax}
                  {...form.getInputProps('fax')}
                />
              </Grid.Col>
            </Grid>
            <Space />
            <Box
              my='md'
              mt={30}
              style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Button type='submit' loading={loading}>
                Submit
              </Button>
            </Box>
          </form>
        )}
      </Container>
    </>
  );
}

const SENTINEL = -999;
const labels = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  streetAddress: 'Street address',
  bldgNameNo: 'Building name and number',
  city: 'City',
  province: 'Province',
  barangay: 'Barangay',
  postalCode: 'Postal code',
  country: 'Country',
  contactNumber: 'Primary contact number',
  fax: 'FAX',
  mobileNumber: 'Mobile phone number',
  emailAddress: 'Email address',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  pin: 'Terminal PIN',
};
const placeholders = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  streetAddress: 'Street address',
  bldgNameNo: 'Building name and number',
  city: 'City',
  province: 'Province',
  barangay: 'Barangay',
  postalCode: 'Postal code',
  country: 'Country',
  contactNumber: 'Primary contact number',
  fax: 'FAX',
  mobileNumber: 'Mobile phone number',
  emailAddress: 'Email address',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  pin: 'Terminal PIN',
};

const ResponsiveCol444 = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col xs={12} sm={4}>
      {children}
    </Grid.Col>
  );
};

const ResponsiveCol66 = ({ children }: { children: ReactNode }) => {
  return <Grid.Col sm={6}>{children}</Grid.Col>;
};

const ResponsiveCol3333 = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col sm={6} lg={3}>
      {children}
    </Grid.Col>
  );
};

export default AdminUpdateUser;
